import { useEffect, useMemo, useState } from "react";

import {
  IMPORTED_REPORTING_CONTEXT_PATH,
  mergeImportedReportingContextCampaigns,
  resolveDefaultAnalysisTimeframe,
} from "../services/analyticsToplineService";
import { readStoredAnalyticsTimeframe } from "../utils/analyticsAggregateTopline";

const DEFAULT_PRODUCTION_BACKEND_URL = "https://cphere-backend.onrender.com";
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === "production" ? DEFAULT_PRODUCTION_BACKEND_URL : "");
const safeBase = (base) => String(base || "").replace(/\/+$/, "");

/*
 * The canonical advertiser reporting scope — `advertiser_reporting_scope_v1`.
 * ALL CAMPAIGNS is an advertiser scope, so its window is resolved once on the
 * server from the whole eligible portfolio and rendered here. This hook does
 * NOT derive a window from a campaign it picked itself: doing that is how a
 * completed campaign's flight end became the advertiser's reporting cutoff and
 * four days of governed delivery fell out of the estate total.
 */
const REPORTING_SCOPE_PATH = "/api/dashboard/reporting-scope";

function getAuthToken() {
  return localStorage.getItem("clientAuthToken") || localStorage.getItem("authToken") || "";
}

function coerceCampaignArray(payload) {
  if (Array.isArray(payload?.campaigns)) return payload.campaigns;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function extractAdvertiserFlightRange(advertiser) {
  if (!advertiser || typeof advertiser !== "object") return null;
  const start = String(
    advertiser.flight_start_date ||
      advertiser.flightStartDate ||
      advertiser.flight_start ||
      advertiser.flightStart ||
      advertiser.start_date ||
      advertiser.startDate ||
      ""
  )
    .trim()
    .slice(0, 10);
  const end = String(
    advertiser.flight_end_date ||
      advertiser.flightEndDate ||
      advertiser.flight_end ||
      advertiser.flightEnd ||
      advertiser.end_date ||
      advertiser.endDate ||
      ""
  )
    .trim()
    .slice(0, 10);
  return start && end ? { start, end } : null;
}

export default function useResolvedAnalyticsTimeframe(advertiserId, advertiserRaw = null) {
  const storedTimeframe = useMemo(() => readStoredAnalyticsTimeframe(), []);
  const activeScopeKey =
    advertiserId === null || advertiserId === undefined ? "" : String(advertiserId);
  const [campaignState, setCampaignState] = useState({
    scopeKey: "",
    campaigns: [],
  });
  const [reportingScopeState, setReportingScopeState] = useState({
    scopeKey: "",
    scope: null,
  });
  const campaigns = useMemo(
    () =>
      campaignState.scopeKey === activeScopeKey ? campaignState.campaigns : [],
    [activeScopeKey, campaignState]
  );
  /*
   * Scoped by advertiser exactly as the campaign list is: a window resolved for
   * one advertiser must never survive a switch to another, which is the same
   * reason the campaign array is gated on `scopeKey`.
   */
  const reportingScope = useMemo(
    () => (reportingScopeState.scopeKey === activeScopeKey ? reportingScopeState.scope : null),
    [activeScopeKey, reportingScopeState]
  );
  const advertiserFlightRange = useMemo(
    () => (activeScopeKey ? extractAdvertiserFlightRange(advertiserRaw) : null),
    [activeScopeKey, advertiserRaw]
  );

  useEffect(() => {
    let cancelled = false;
    const scopeKey =
      advertiserId === null || advertiserId === undefined ? "" : String(advertiserId);

    async function loadCampaigns() {
      if (!advertiserId || !API_BASE_URL) {
        if (!cancelled) {
          setCampaignState({ scopeKey, campaigns: [] });
        }
        return;
      }

      const token = getAuthToken();
      if (!token) {
        if (!cancelled) {
          setCampaignState({ scopeKey, campaigns: [] });
        }
        return;
      }

      try {
        setCampaignState({ scopeKey, campaigns: [] });
        const query = new URLSearchParams({
          advertiserId: String(advertiserId),
          ts: String(Date.now()),
        });
        const response = await fetch(
          `${safeBase(API_BASE_URL)}/api/media-campaigns?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
            cache: "no-store",
          }
        );
        // Imported reporting context: client-visible lists hide reporting-only
        // rows, but the analytics timeframe still needs the imported flight.
        const importedContextResponse = await fetch(
          `${safeBase(API_BASE_URL)}${IMPORTED_REPORTING_CONTEXT_PATH}?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
            cache: "no-store",
          }
        ).catch(() => null);
        const [payload, importedContextPayload] = await Promise.all([
          response.json().catch(() => null),
          importedContextResponse?.ok
            ? importedContextResponse.json().catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setCampaignState({
          scopeKey,
          campaigns: response.ok
            ? mergeImportedReportingContextCampaigns(
                coerceCampaignArray(payload),
                importedContextPayload
              )
            : [],
        });
      } catch (error) {
        if (cancelled) return;
        console.error("[useResolvedAnalyticsTimeframe] failed to resolve campaigns", error);
        setCampaignState({ scopeKey, campaigns: [] });
      }
    }

    loadCampaigns();

    return () => {
      cancelled = true;
    };
  }, [advertiserId]);

  useEffect(() => {
    let cancelled = false;
    const scopeKey =
      advertiserId === null || advertiserId === undefined ? "" : String(advertiserId);

    async function loadReportingScope() {
      const token = advertiserId && API_BASE_URL ? getAuthToken() : "";
      if (!token) {
        if (!cancelled) setReportingScopeState({ scopeKey, scope: null });
        return;
      }

      try {
        setReportingScopeState({ scopeKey, scope: null });
        const query = new URLSearchParams({
          advertiserId: String(advertiserId),
          ts: String(Date.now()),
        });
        // No viewer zone is sent: the backend resolves "through today" on the
        // reporting calendar (Eastern Time), the same day for every viewer.
        const response = await fetch(
          `${safeBase(API_BASE_URL)}${REPORTING_SCOPE_PATH}?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
            cache: "no-store",
          }
        );
        const payload = response.ok ? await response.json().catch(() => null) : null;
        if (cancelled) return;
        /*
         * Fail closed to "no governed window" rather than to a guess. With no
         * scope the resolver keeps its previous approved-flight behaviour —
         * degraded, but never a window this hook invented.
         */
        setReportingScopeState({
          scopeKey,
          scope: payload?.window?.start && payload?.window?.end ? payload : null,
        });
      } catch (error) {
        if (cancelled) return;
        console.error("[useResolvedAnalyticsTimeframe] failed to resolve reporting scope", error);
        setReportingScopeState({ scopeKey, scope: null });
      }
    }

    loadReportingScope();

    return () => {
      cancelled = true;
    };
  }, [advertiserId]);

  const timeframe = useMemo(
    () =>
      resolveDefaultAnalysisTimeframe({
        timeframe: storedTimeframe,
        campaigns,
        advertiserFlightRange,
        advertiserReportingWindow: reportingScope,
      }),
    [advertiserFlightRange, campaigns, reportingScope, storedTimeframe]
  );

  return {
    advertiserFlightRange,
    campaigns,
    reportingScope,
    storedTimeframe,
    timeframe,
  };
}
